import { findFilteredDifferences } from "@/lib/prelude";
import { OrganizationMember } from "@/lib/api/betterAuth";

export interface AuthUserUpdatableFields {
  name?: string | null;
}

function transformToAuthUserComparable(
  authUser: OrganizationMember["authuser"] | AuthUserUpdatableFields | undefined
): AuthUserUpdatableFields {
  if (!authUser) return {};
  return {
    name: authUser.name,
  };
}

/**
 * Derive update payload by comparing original and new values
 * Returns only fields that changed, with removed fields set to null
 */
export function deriveAuthUserUpdateFields(
  original: OrganizationMember["authuser"] | undefined,
  updated: AuthUserUpdatableFields
): AuthUserUpdatableFields | undefined {
  const originalComparable = transformToAuthUserComparable(original);
  const updatedComparable = transformToAuthUserComparable(updated);

  // Find new and changed fields
  const changedFields = findFilteredDifferences(updatedComparable, originalComparable, {
    new: true,
    changed: true,
  });

  // Find removed fields (fields in original but not in updated)
  const removedFields = findFilteredDifferences(updatedComparable, originalComparable, {
    removed: true,
  });

  // If no changes, return undefined
  if (changedFields.length === 0 && removedFields.length === 0) {
    return undefined;
  }

  const updatePayload: AuthUserUpdatableFields = {};

  // Add changed/new fields with their new values
  changedFields.forEach((field) => {
    const key = field as keyof AuthUserUpdatableFields;
    updatePayload[key] = updatedComparable[key];
  });

  // Set removed fields to null
  removedFields.forEach((field) => {
    const key = field as keyof AuthUserUpdatableFields;
    updatePayload[key] = null;
  });

  return updatePayload;
}

